import { ArrowUpCircle } from 'lucide-react';
import { cn } from '../lib/utils.js';
import { focusRing } from '../lib/focus.js';
import { ICON } from '../lib/iconSize.js';

export interface UpdateRequiredLabels {
  /** Heading — e.g. "Update required". */
  title: string;
  /** One or two sentences explaining why the app is blocked. */
  description: string;
  /** Row label for the running build — e.g. "Your version". */
  currentLabel: string;
  /** Row label for the required floor — e.g. "Required". */
  requiredLabel: string;
  /** Call-to-action on the update button — e.g. "Get the latest version". */
  updateCta: string;
}

const DEFAULT_LABELS: UpdateRequiredLabels = {
  title: 'Update required',
  description:
    'This version of the app is no longer supported. Please update to the latest version to continue.',
  currentLabel: 'Your version',
  requiredLabel: 'Required',
  updateCta: 'Get the latest version',
};

export interface UpdateRequiredScreenProps {
  /** The running build's version, shown to the user. */
  currentVersion: string;
  /** The minimum version the server requires, when known. */
  minVersion?: string | null;
  /** Where the CTA sends the user; the CTA is hidden when absent. */
  updateUrl?: string | null;
  /** Copy overrides for i18n; English defaults fill any gaps. */
  labels?: Partial<UpdateRequiredLabels>;
  className?: string;
}

/**
 * Full-screen, non-dismissible block shown when the running app is older than
 * the minimum version the server accepts (see the version gate wired into each
 * app's entry point). Purely presentational — all of the "is this the latest
 * version?" logic lives in `@attendance/shared` (`checkAppVersion`); this only
 * renders the dead end and the way out. Deliberately dependency-light so it can
 * render even when the rest of the app hasn't (or shouldn't) mount.
 *
 * STYLING CONSTRAINT — this is the only primitive rendered by all three apps
 * (web, kiosk, installer), and the three do not share a Tailwind config. It
 * therefore restricts itself to the intersection: stock Tailwind utilities plus
 * the semantic colours all three map. It previously reached for `shadow-m3-1`,
 * `text-title-lg`, `bg-surface-container-high` and `.state-layer`, none of
 * which exist outside apps/web — so on the kiosk and the installer this screen
 * was rendering with no shadow, no heading size and no hover feedback, on the
 * one screen a user sees when they are already stuck. Keep it boring.
 */
export function UpdateRequiredScreen({
  currentVersion,
  minVersion,
  updateUrl,
  labels,
  className,
}: UpdateRequiredScreenProps): JSX.Element {
  const l = { ...DEFAULT_LABELS, ...labels };
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="update-required-title"
      className={cn(
        'fixed inset-0 z-[9999] flex items-center justify-center bg-background/95 p-6 backdrop-blur-sm',
        className
      )}
    >
      <div className="w-full max-w-sm rounded-modal border border-border bg-surface p-6 text-center shadow-xl">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
          <ArrowUpCircle size={ICON.hero} aria-hidden />
        </div>
        <h1 id="update-required-title" className="text-h2 text-foreground">
          {l.title}
        </h1>
        <p className="mt-2 text-copy text-muted-foreground">{l.description}</p>

        <dl className="mt-5 space-y-1.5 rounded-card bg-muted px-4 py-3 text-copy">
          <div className="flex items-center justify-between gap-4">
            <dt className="text-muted-foreground">{l.currentLabel}</dt>
            <dd className="font-mono font-medium text-foreground">{currentVersion}</dd>
          </div>
          {minVersion ? (
            <div className="flex items-center justify-between gap-4">
              <dt className="text-muted-foreground">{l.requiredLabel}</dt>
              <dd className="font-mono font-medium text-foreground">{minVersion}</dd>
            </div>
          ) : null}
        </dl>

        {updateUrl ? (
          <a
            href={updateUrl}
            className={`mt-5 inline-flex min-h-touch w-full items-center justify-center rounded-control bg-primary px-6 text-label font-semibold text-primary-foreground transition-opacity hover:opacity-90 ${focusRing}`}
          >
            {l.updateCta}
          </a>
        ) : null}
      </div>
    </div>
  );
}
